import React from 'react';
import { Award, Heart, Users, Sparkles, Quote } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen bg-[#FEFCF9]">
      {/* Hero */}
      <section className="relative py-14 sm:py-18 lg:py-24 bg-[#F5EFE6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-[10px] sm:text-xs tracking-[0.3em] text-[#C4A265] uppercase font-medium mb-3 sm:mb-4">About Us</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-serif text-[#3E2723] mb-4 sm:mb-6">Our Story</h1>
            <div className="w-12 h-px bg-[#C4A265] mx-auto mb-6"></div>
            <p className="text-lg text-[#7A6B5D] leading-relaxed max-w-2xl mx-auto">
              A celebration of India's rich heritage through exquisite handcrafted home decor
            </p>
          </div>
        </div>
      </section>

      {/* Logo + Description */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#FEFCF9]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-[#F5EFE6] p-6 sm:p-8 lg:p-12 border border-[#E8DCC8] relative mb-8 sm:mb-10">
            <div className="absolute -top-1.5 -left-1.5 w-16 h-16 border-t-2 border-l-2 border-[#C4A265]"></div>
            <div className="absolute -bottom-1.5 -right-1.5 w-16 h-16 border-b-2 border-r-2 border-[#C4A265]"></div>
            <img
              src="https://customer-assets.emergentagent.com/job_behad-living/artifacts/rxtdz05y_Behat_Atelier.png"
              alt="Behat House Atelier Logo"
              className="w-48 sm:w-64 lg:w-72 xl:w-80 h-auto object-contain mx-auto"
            />
          </div>
          <p className="text-[#7A6B5D] leading-relaxed max-w-2xl mx-auto text-base">
            The logo — an arch and a tulip — combines the strength and endurance symbolized by the arch with the beauty and elegance associated with the tulip. 
            This design embodies a harmonious blend of architectural significance and floral symbolism, representing resilience, growth, and the beauty of cultural heritage.
          </p>
        </div>
      </section>

      {/* Opening Quote */}
      <section className="py-12 bg-[#FEFCF9]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <Quote className="absolute -top-3 -left-1 w-10 h-10 text-[#C4A265] opacity-25" />
            <div className="border-l-2 border-[#C4A265] pl-8 ml-4">
              <p className="text-xl font-serif text-[#3E2723] leading-relaxed italic">
                A premium brand of home decor and soft furnishings, Behat House is an embodiment of our rich traditions, 
                steeped in fine taste and unmatched luxury.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Sections — no icons */}
      <section className="py-16 bg-[#FEFCF9]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">

          <div className="text-center">
            <h2 className="text-2xl font-serif text-[#3E2723] mb-4">Our Essence</h2>
            <div className="w-10 h-px bg-[#C4A265] mx-auto mb-5"></div>
            <p className="text-[#7A6B5D] leading-relaxed max-w-2xl mx-auto">
              Every piece we create tells the story of India's magnificent culture and skilled craftsmanship. 
              Each creation is a testament to centuries of tradition, brought to life through the hands of master artisans.
            </p>
          </div>

          <div className="text-center">
            <h2 className="text-2xl font-serif text-[#3E2723] mb-4">Our Mission</h2>
            <div className="w-10 h-px bg-[#C4A265] mx-auto mb-5"></div>
            <p className="text-[#7A6B5D] leading-relaxed max-w-2xl mx-auto">
              Behat House has been envisioned as a platform to <span className="font-semibold text-[#3E2723]">revive, preserve, and celebrate</span> indigenous 
              art and craft, one breathtaking collection at a time.
            </p>
          </div>

          {/* Featured Quote */}
          <div className="bg-[#3E2723] p-10 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#C4A265] opacity-5 rounded-full blur-2xl"></div>
            <Quote className="w-8 h-8 text-[#C4A265] mx-auto mb-4 opacity-60" />
            <p className="text-lg font-serif leading-relaxed text-[#F5EFE6] max-w-2xl mx-auto italic">
              "At Behat House, we don't only want to add value to your homes and lives with our products, 
              but also breathe new life into dying, age-old crafts."
            </p>
          </div>

          <div className="text-center">
            <h2 className="text-2xl font-serif text-[#3E2723] mb-4">Collectible Masterpieces</h2>
            <div className="w-10 h-px bg-[#C4A265] mx-auto mb-5"></div>
            <p className="text-[#7A6B5D] leading-relaxed max-w-2xl mx-auto">
              Every offering from our atelier is a collectible masterpiece — handmade by ingenious artisans, featuring exquisite 
              techniques like zardozi, chikankari, aari, suzani, nakkashi, and kalamkari.
            </p>
          </div>

          <div className="text-center">
            <h2 className="text-2xl font-serif text-[#3E2723] mb-4">Our Philosophy</h2>
            <div className="w-10 h-px bg-[#C4A265] mx-auto mb-5"></div>
            <p className="text-[#7A6B5D] leading-relaxed max-w-2xl mx-auto mb-4">
              Aligned with the ethos of slow fashion and sustainability, Behat House embraces 
              exclusivity over mass production, ensuring that each creation is a unique labour of love.
            </p>
            <p className="text-[#7A6B5D] leading-relaxed max-w-2xl mx-auto">
              It is also an homage to the glorious past, and with immense appreciation for the modern — intricately weaving 
              together the threads of history and heritage.
            </p>
          </div>
        </div>
      </section>

      {/* Values — no icons */}
      <section className="py-10 sm:py-16 lg:py-20 bg-[#F5EFE6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.3em] text-[#C4A265] uppercase font-medium mb-2">What We Stand For</p>
            <h2 className="text-3xl font-serif text-[#3E2723]">Our Values</h2>
            <div className="w-12 h-px bg-[#C4A265] mx-auto mt-4"></div>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              { title: 'Heritage Excellence', desc: 'Preserving and celebrating centuries-old traditional craft techniques' },
              { title: 'Artisan Partnership', desc: 'Empowering master craftspeople and supporting their communities' },
              { title: 'Sustainable Luxury', desc: 'Slow fashion principles ensuring quality over quantity' },
              { title: 'Unique Creations', desc: 'Every piece is a one-of-a-kind collectible masterpiece' }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <h3 className="text-lg font-serif text-[#3E2723] mb-3">{item.title}</h3>
                <div className="w-8 h-px bg-[#C4A265] mx-auto mb-3"></div>
                <p className="text-sm text-[#7A6B5D]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing Invitation */}
      <section className="py-16 bg-[#FEFCF9] border-t border-[#E8DCC8]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h3 className="text-2xl font-serif text-[#3E2723] mb-4">An Invitation</h3>
          <p className="text-[#7A6B5D] leading-relaxed">
            When you invite Behat House into your home, you're embracing extravagance, 
            preserving traditions, and becoming part of a majestic journey through time.
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;
